<script lang="ts" setup>
import {
  YandexMap,
  YandexMapClusterer,
  YandexMapControls,
  YandexMapDefaultFeaturesLayer,
  YandexMapDefaultSchemeLayer,
  YandexMapGeolocationControl,
  YandexMapListener,
  YandexMapZoomControl,
} from 'vue-yandex-maps';
import { debounce } from '@antfu/utils';
import type { Location, PickupPoint } from '~/types/Checkout';
import type { YMapLocation } from '@yandex/ymaps3-types/imperative/YMap';
import type { MapEventUpdateHandler, YMap } from '@yandex/ymaps3-types';
import type { YMapClusterer } from '@yandex/ymaps3-types/packages/clusterer';

const props = defineProps<{ points: PickupPoint[]; selectedLocation: Location }>();

const map = shallowRef<YMap | null>(null);
const clusterer = shallowRef<YMapClusterer | undefined>(undefined);
const localLocation = shallowRef<YMapLocation | null>(null);

const checkoutStore = useCheckoutStore();
const { openMarker } = storeToRefs(checkoutStore);

const defaultCenter = computed<[lon: number, lat: number]>(() =>
  props.selectedLocation
    ? [Number(props.selectedLocation.data.geo_lon), Number(props.selectedLocation.data.geo_lat)]
    : [37.6142, 55.7498],
);
const currentZoom = ref(11);
const duration = 500;

watch(
  () => props.selectedLocation,
  () => {
    currentZoom.value = 11;
    localLocation.value = null;
    openMarker.value = null;
  },
);

watch(
  () => openMarker.value,
  () => {
    if (openMarker.value !== null) {
      currentZoom.value = currentZoom.value > 16 ? currentZoom.value : 16;
      localLocation.value = {
        center: [Number(openMarker.value.coord_x), Number(openMarker.value.coord_y)],
        zoom: currentZoom.value,
        duration,
      };
    }
  },
);

const handleMapUpdate: MapEventUpdateHandler = (object) => {
  if (object.location.zoom !== currentZoom.value) {
    localLocation.value = object.location;
    currentZoom.value = object.location.zoom;
  }
};

const debouncedMapUpdateHandle = debounce(500, handleMapUpdate);
</script>

<template>
  <YandexMap
    v-model="map"
    :settings="{
      location: localLocation || {
        center: defaultCenter,
        zoom: currentZoom,
      },
      behaviors: ['drag', 'pinchZoom', 'dblClick', 'oneFingerZoom'],
    }"
    class="rounded-xl overflow-hidden"
    real-settings-location
  >
    <YandexMapDefaultSchemeLayer />
    <YandexMapDefaultFeaturesLayer />
    <YandexMapListener :settings="{ onUpdate: debouncedMapUpdateHandle }" />

    <YandexMapControls :settings="{ position: 'right' }">
      <YandexMapZoomControl />
      <YandexMapGeolocationControl />
    </YandexMapControls>

    <YandexMapClusterer
      v-if="currentZoom < 17"
      v-model="clusterer"
      :grid-size="currentZoom <= 14 ? 64 : 32"
      zoom-on-cluster-click
    >
      <template #default>
        <PickupPointItemOnMap
          v-for="point in points"
          :key="point.code"
          :is-show-price="currentZoom > 13"
          :pickup-point="point"
        />
      </template>

      <template #cluster="{ length }">
        <div class="ym-cluster">
          {{ length }}
        </div>
      </template>
    </YandexMapClusterer>

    <template v-else>
      <PickupPointItemOnMap
        v-for="point in points"
        :key="point.code"
        :is-show-price="currentZoom > 13"
        :pickup-point="point"
      />
    </template>
  </YandexMap>
</template>

<style lang="scss" scoped>
.ym-cluster {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  border: 3px solid $white;
  border-radius: 100%;
  background-color: $hell-black;
  color: $white;
  box-shadow: 0 4px 4px 0 rgb(0 0 0 / 25%);
  font-weight: $bold;
  cursor: pointer;
}
</style>
